import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePasswordRecovery } from "hooks/accountManagement/useAccountRecovery";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { toast } from "react-toastify";

import logo from "../../../assets/img/illustrations/logo.png";

const CreateNewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetToken } = location.state || {};

  const { resetPassword, loading, error } = usePasswordRecovery();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    if (!newPassword || !confirmPassword) {
      return setValidationError("Please fill both fields");
    }
    if (newPassword !== confirmPassword) {
      return setValidationError("Passwords do not match");
    }
    if (newPassword.length < 8) {
      return setValidationError("Password must be at least 8 characters");
    }
    if (!resetToken) {
      return setValidationError("Invalid link");
    }

    try {
      await resetPassword({ resetToken, newPassword, confirmPassword });
      toast.success("Password updated successfully.");
      setSuccess(true);
      setTimeout(() => navigate("/auth/login"), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update password.");
    }
  };

  return (
    <div className="min-vh-100 bg-white d-flex flex-column align-items-center justify-content-center px-3">

      {/* Logo */}
      <div className="mb-4">
        <img src={logo} alt="Logo" style={{ height: "56px", objectFit: "contain" }} />
      </div>

      {/* Compact Card */}
      <div className="bg-white rounded-3 shadow-sm p-4" style={{ maxWidth: "420px", width: "100%" }}>

        <div className="text-center mb-4">
          <h4 className="fw-bold mb-1">Create New Password</h4>
        </div>

        {validationError && <Alert variant="danger" className="small py-2">{validationError}</Alert>}
        {error && <Alert variant="danger" className="small py-2">{error}</Alert>}
        {success && <Alert variant="success" className="small py-2">Password updated! Redirecting...</Alert>}

        <Form onSubmit={handleSubmit} className="mt-3">

          {/* New Password */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium small">New Password</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                disabled={loading || success}
                style={{ height: "44px" }}
                className="pe-5"
              />
              <Button
                variant="link"
                className="position-absolute end-0 top-50 translate-middle-y text-muted"
                style={{ zIndex: 10 }}
                onClick={() => setShowNew(!showNew)}
                type="button"
                disabled={loading || success}
              >
                {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
              </Button>
            </div>
          </Form.Group>

          {/* Confirm Password */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-medium small">Confirm Password</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading || success}
                style={{ height: "44px" }}
                className="pe-5"
              />
              <Button
                variant="link"
                className="position-absolute end-0 top-50 translate-middle-y text-muted"
                style={{ zIndex: 10 }}
                onClick={() => setShowConfirm(!showConfirm)}
                type="button"
                disabled={loading || success}
              >
                {showConfirm ? <EyeSlash size={18} /> : <Eye size={18} />}
              </Button>
            </div>
          </Form.Group>

          {/* Compact Green Button */}
          <Button
            variant="success"
            type="submit"
            className="w-100 fw-semibold"
            disabled={loading || success || !newPassword || !confirmPassword}
            style={{
              backgroundColor: "#28a745",
              border: "none",
              height: "46px",
              fontSize: "1rem"
            }}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : success ? (
              "Success! Redirecting..."
            ) : (
              "Set New Password"
            )}
          </Button>
        </Form>

        <div className="text-center mt-3">
          <Button
            variant="link"
            onClick={() => navigate("/auth/login")}
            disabled={loading || success}
            className="text-muted small"
          >
            Back to login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewPassword;
